<template>
  <div class="fiber-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>入射角 θ</label>
            <span class="val">{{ theta.toFixed(1) }}°</span>
          </div>
          <div class="hud-item orange">
            <label>反射次数 (级联)</label>
            <span class="val">{{ reflectCount }}</span>
          </div>
          <div class="hud-item green">
             <label>信号状态</label>
             <span class="val">{{ isSuccess ? '信号完整传输' : '信号逃逸/损耗' }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-fiber"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 光纤包层 (Cladding) -->
          <rect x="0" y="80" width="450" height="80" fill="#1e293b" opacity="0.4" />
          <line x1="0" y1="80" x2="450" y2="80" stroke="#334155" stroke-dasharray="4,4" />
          <line x1="0" y1="160" x2="450" y2="160" stroke="#334155" stroke-dasharray="4,4" />

          <!-- 光纤芯 (Core) -->
          <rect x="0" y="95" width="450" height="50" fill="rgba(14, 165, 233, 0.05)" />
          <line x1="0" y1="95" x2="450" y2="95" stroke="#0ea5e9" opacity="0.3" />
          <line x1="0" y1="145" x2="450" y2="145" stroke="#0ea5e9" opacity="0.3" />

          <!-- 导光路径 (Guided Light Path) -->
          <path :d="fiberPath" fill="none" :stroke="isSuccess ? '#fbbf24' : '#f43f5e'" stroke-width="3" filter="url(#glow-fiber)" />
          
          <text x="20" y="70" fill="#64748b" font-size="9">包层 (n₂ 低)</text>
          <text x="20" y="110" fill="#0ea5e9" font-size="9" font-weight="900">纤芯 (n₁ 高)</text>
        </svg>

        <div class="physics-caption">
           原理：光信号在纤芯与包层的界面上发生全反射，像“水流”一样顺着导管前进。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节信号入耦合角度 (θ)：{{ theta.toFixed(1) }}°</label>
                 <el-slider v-model="theta" :min="0" :max="45" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="toggleFiberCurve">点击弯曲纤维</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-discovery-tir" v-latex>
           <div class="icon-wrap"><i class="el-icon-link"></i></div>
           <div class="text-content">
             <p><strong>实战应用：</strong>这就是光导纤维。<strong>解惑：</strong>只要满足 $n_1 > n_2$ 且入射角足够大，光就被“锁”在心部。<strong>观察：</strong>即使纤维弯折，光路依然会跟随反射。这是带宽极高、零电磁干扰的通信命脉。如果角度过小，光会穿过界面进入包层（变红），代表信号丢失（散耗）。</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'FiberOpticCommunicationViz',
  data() {
    return {
      theta: 25,
      isCurve: false
    }
  },
  computed: {
    criticalLimit() { return 38; }, // Sample limit
    isSuccess() { return this.theta < this.criticalLimit; },
    reflectCount() { return Math.floor(450 / (50 / Math.tan(this.theta * Math.PI / 180))) + 1 || 0; },
    fiberPath() {
       let p = "M 0,120 ";
       const L = 450;
       const yCenter = 120;
       const dY = 50 / 2;
       
       const rad = this.theta * Math.PI / 180;
       const dx = 25 / Math.tan(rad);
       
       let currentX = 0;
       let currentY = 120;
       let direction = this.theta > 0 ? -1 : 0;

       for(let i=0; i<15; i++) {
          if (currentX > L) break;
          // zigzag logic
          currentX += dx;
          currentY = yCenter + direction * dY;
          p += `L ${currentX},${currentY} `;
          direction *= -1;
       }
       return p;
    }
  },
  methods: {
    toggleFiberCurve() { this.isCurve = !this.isCurve; }
  }
}
</script>

<style scoped lang="scss">
.fiber-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.green { border-left: 3px solid #10b981; } } }
.physics-caption { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-discovery-tir { background: #eff6ff; padding: 12px 16px; border-radius: 10px; border: 1px solid #bfdbfe; display: flex; gap: 14px; align-items: center; i { color: #1e40af; font-size: 1.4rem; } p { margin: 0; font-size: 11.5px; color: #1e3a8a; line-height: 1.6; strong { color: #1e3a8a; font-weight: 900; } } }
</style>
